import { InvoiceKind, InvoiceV2Output } from '@allocab/allocab-invoice-service-types';

import { InvoiceStateBadge } from './InvoiceStateBadge';
import { InvoiceV2DownloadButton } from './InvoiceV2DownloadButton';
import { TableColumn } from '../../components/table/types';
import { TimeScope, TimeScopeMode } from '../../components/time-scope-picker/types';
import { constants } from '../../constants';
import { t } from '../../services/translation/translation';
import { assertNever } from '../../utils/common';
import { formatDateForParisTZ } from '../../utils/date';
import { formatPrice } from '../../utils/price';

export const formatTimeScopeToInput = (scope: TimeScope, mode: TimeScopeMode) => {
  return {
    ...(mode === 'day' ? { referenceDay: scope.day } : {}),
    ...(mode === 'week' ? { referenceWeek: scope.week } : {}),
    ...(mode === 'fortnight' ? { referenceFortnight: scope.fortnight } : {}),
    ...(mode === 'day' || mode === 'fortnight' || mode === 'month' ? { referenceMonth: scope.month + 1 } : {}),
    referenceYear: scope.year,
  };
};

export const invoicesColumns: TableColumn<InvoiceV2Output>[] = [
  {
    name: t('common.name'),
    render: invoice => (
      <div className="inline-flex flex-col items-start w-36">
        <span>{invoice.invoiceNumber}</span>
        <span className="text-sm opacity-50 font-normal">
          {formatDateForParisTZ(invoice.createdAt, t('date.date-short'), false)}
        </span>
      </div>
    ),
    sortKey: 'invoiceNumber',
  },
  {
    name: t('common.status'),
    render: invoice => <InvoiceStateBadge state={invoice.state} />,
    sortKey: 'state',
  },
  {
    name: t('common.type'),
    render: invoice => getTextForKind(invoice.invoiceKind),
    sortKey: 'invoiceKind',
  },
  {
    name: t('common.effective-payed-at'),
    render: invoice =>
      invoice.effectivePaymentDate
        ? formatDateForParisTZ(invoice.effectivePaymentDate, t('date.date-short'), false)
        : '--',
    sortKey: 'effectivePaymentDate',
  },
  {
    name: t('common.amount'),
    render: invoice => (
      <div className="inline-flex flex-col items-start w-36">
        {(invoice.invoiceType === 'CREDIT_MEMO' && invoice.invoiceKind === 'DRIVER_TRIP_INVOICE') ||
        invoice.emitterAccountKey === constants.allocabAccountKey ||
        invoice.invoiceKind === 'PENALTY_INVOICE' ? (
          <span className="font-mono text-red-600">
            {invoice.total.amountWithTax < 0
              ? formatPrice(invoice.total.amountWithTax)
              : `-${formatPrice(invoice.total.amountWithTax)}`}
          </span>
        ) : (
          <span>{formatPrice(invoice.total.amountWithTax)}</span>
        )}
        {invoice.total.vat?.[10]?.amount ? (
          <span className="text-[10px] opacity-50 font-normal">
            {t('invoicing-route.vat-included', { price: formatPrice(invoice.total.vat[10].amount) })}
          </span>
        ) : null}
      </div>
    ),
    sortKey: 'effectivePaymentDate',
  },
  {
    name: '',
    render: invoice => <InvoiceV2DownloadButton invoice={invoice} />,
  },
];

export const getTextForKind = (kind: InvoiceKind) => {
  switch (kind) {
    case 'DRIVER_PACKAGE_INVOICE':
      return t('invoice-kind.package');
    case 'DRIVER_TRIP_INVOICE':
      return t('invoice-kind.trip');
    case 'PENALTY_INVOICE':
      return t('invoice-kind.penalty');
    case 'DRIVER_VAO_CAR_INVOICE':
      return t('invoice-kind.vao-car-rental');
    case 'PASSENGER_TRIP_INVOICE':
    case 'PASSENGER_CREDIT_IMMEDIATE_INVOICE':
    case 'PASSENGER_PERIODIC_INVOICE':
      return '';
    default:
      assertNever(kind, `[getTextForKind] kind unknown: ${kind}`);
      return '';
  }
};
